import React, { FC, useEffect, useRef, useState } from "react";
import {
  Typography,
  Box,
  Stack,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTheme } from "@mui/material/styles";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckIcon from "@mui/icons-material/Check";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  apifetchNotifications,
  deleteNotification,
  updateNotification,
} from "../api/notification";
import usePageVisibility from "../hooks/usePageVisibility";
import { useAuth } from "../hooks/useAuth";
import useResponsive from "../hooks/useResponsive";
import { useNavigate } from "react-router-dom";

interface NotificationItem {
  created_at: string;
  is_read: boolean;
  message: string;
  priority: string;
  receiver: string;
  reference: string;
  title: string;
  type: string;
  updated_at: string;
  _id: string;
  action_url?: string;
}

const NotificationContent = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isVisible = usePageVisibility();
  const { openNotification, handleNotification, handleIsNotification } =
    useAuth();
  const intervalRef = useRef<number | null>(null);
  const isSmall = useResponsive("down", "md", "lg");
  const [notification_data, setNotification_data] = useState<
    NotificationItem[]
  >([]);

  const queryClient = useQueryClient();

  const { data, refetch } = useQuery({
    queryKey: ["notifications"],
    queryFn: async () => {
      const result = await apifetchNotifications();
      if (Array.isArray(result?.data)) {
        setNotification_data(result?.data);

        if (result.data.length > 0) {
          handleIsNotification(true);
        } else {
          handleIsNotification(false);
        }
        return result?.data;
      } else {
        setNotification_data([]);
        return [];
      }
    },
    enabled: false,
  });

  useEffect(() => {
    if (isVisible) {
      refetch();
      intervalRef.current = window.setInterval(refetch, 30000);
    } else if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    return () => {
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isVisible, refetch]);

  const updateMutation = useMutation({
    mutationFn: (id: string) => updateNotification(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["notifications"],
        exact: true,
      });
      refetch();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteNotification(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["notifications"],
        exact: true,
      });
      refetch();
    },
  });

  const handleDelete = (id: string) => {
    deleteMutation.mutate(id);
  };

  const handleUpdate = (id: string) => {
    updateMutation.mutate(id);
  };

  const handleNavigate = (notification: NotificationItem) => {
    if (notification.action_url) {
      if (!notification.is_read) {
        handleUpdate(notification._id);
      }
      handleNotification();
      navigate(notification.action_url);
    }
  };

  const markAllAsReadMutation = useMutation({
    mutationFn: async () => {
      const unreadIds = notification_data
        .filter((n) => !n.is_read)
        .map((n) => n._id);

      await Promise.all(unreadIds.map((id) => updateNotification(id)));
      return unreadIds;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["notifications"],
        exact: true,
      });
      refetch();
    },
  });

  const clearAllMutation = useMutation({
    mutationFn: async () => {
      const allIds = notification_data.map((n) => n._id);
      await Promise.all(allIds.map((id) => deleteNotification(id)));
      return allIds;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["notifications"],
        exact: true,
      });
      refetch();
    },
  });

  const handleMarkAllAsRead = () => {
    if (notification_data.some((n) => !n.is_read)) {
      markAllAsReadMutation.mutate();
    }
  };

  const handleClearAll = () => {
    if (notification_data.length > 0) {
      clearAllMutation.mutate();
    }
  };

  const unreadCount = notification_data.filter((n) => !n.is_read).length;

  return (
    <>
      {openNotification && (
        <Box
          sx={{
            position: "fixed",
            top: "70px",
            right: "20px",
            width: isSmall ? "calc(100% - 40px)" : 380,
            maxHeight: 500,
            bgcolor: "white",
            boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
            borderRadius: "8px",
            overflow: "hidden",
            zIndex: 1300,
          }}
        >
          {/* Header */}
          <Box
            sx={{
              px: 2.5,
              py: 2,
              borderBottom: "1px solid #f0f0f0",
            }}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 1 }}
            >
              <Typography sx={{ fontSize: 16, fontWeight: 600 }}>
                Notifications
                {unreadCount > 0 && (
                  <Typography
                    component="span"
                    sx={{
                      ml: 1,
                      fontSize: 12,
                      color: "#666",
                      fontWeight: 400,
                    }}
                  >
                    ({unreadCount} unread)
                  </Typography>
                )}
              </Typography>
              <IconButton size="small" onClick={handleNotification}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>
            {notification_data.length > 0 && (
              <Stack direction="row" spacing={1}>
                <Typography
                  onClick={handleMarkAllAsRead}
                  sx={{
                    fontSize: 12,
                    color: theme.palette.primary.main,
                    cursor: unreadCount > 0 ? "pointer" : "default",
                    opacity: unreadCount > 0 ? 1 : 0.4,
                    "&:hover": {
                      textDecoration: unreadCount > 0 ? "underline" : "none",
                    },
                  }}
                >
                  Mark all as read
                </Typography>
                <Typography sx={{ fontSize: 12, color: "#ccc" }}>•</Typography>
                <Typography
                  onClick={handleClearAll}
                  sx={{
                    fontSize: 12,
                    color: theme.palette.error.main,
                    cursor: "pointer",
                    "&:hover": {
                      textDecoration: "underline",
                    },
                  }}
                >
                  Clear all
                </Typography>
              </Stack>
            )}
          </Box>

          {/* List */}
          <Box
            sx={{
              maxHeight: 440,
              overflowY: "auto",
              "&::-webkit-scrollbar": { width: "6px" },
              "&::-webkit-scrollbar-thumb": {
                bgcolor: "#e0e0e0",
                borderRadius: "3px",
              },
            }}
          >
            {notification_data?.length === 0 ? (
              <Box
                sx={{
                  py: 8,
                  textAlign: "center",
                  color: "#999",
                }}
              >
                <Typography sx={{ fontSize: 14 }}>No notifications</Typography>
              </Box>
            ) : (
              <List sx={{ p: 0 }}>
                {notification_data.map((notification) => (
                  <ListItem
                    key={notification._id}
                    sx={{
                      px: 2.5,
                      py: 1.5,
                      borderBottom: "1px solid #f8f8f8",
                      bgcolor: notification.is_read ? "white" : "#f9fafb",
                      cursor: notification.action_url ? "pointer" : "default",
                      "&:hover": {
                        bgcolor: "#f5f5f5",
                      },
                      "&:last-child": {
                        borderBottom: "none",
                      },
                    }}
                    onClick={() => handleNavigate(notification)}
                  >
                    {/* Blue dot for unread */}
                    {!notification.is_read && (
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          bgcolor: theme.palette.primary.main,
                          mr: 1.5,
                          flexShrink: 0,
                        }}
                      />
                    )}

                    <ListItemText
                      sx={{ m: 0 }}
                      primary={
                        <Typography
                          sx={{
                            fontSize: 14,
                            fontWeight: notification.is_read ? 400 : 600,
                            mb: 0.5,
                          }}
                        >
                          {notification.title}
                        </Typography>
                      }
                      secondary={
                        <Typography
                          sx={{
                            fontSize: 13,
                            color: "#666",
                            lineHeight: 1.4,
                          }}
                        >
                          {notification.message}
                        </Typography>
                      }
                    />

                    {/* Actions */}
                    <Stack direction="row" spacing={0.5} sx={{ ml: 1 }}>
                      {notification.action_url && (
                        <IconButton size="small">
                          <ArrowForwardIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      )}
                      {!notification.is_read && (
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleUpdate(notification._id);
                          }}
                        >
                          <CheckIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      )}
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(notification._id);
                        }}
                      >
                        <DeleteIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Stack>
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        </Box>
      )}
    </>
  );
};

export default NotificationContent;
